import { localizeHref } from 'modules/locale/history';
import { getLocaleFromPath } from 'modules/locale/paths';
import { forwardRef } from 'react';
import { useActive, useLinkProps } from 'react-navi';
import type { LinkProps } from 'react-navi/dist/types/Link';

// react-navi `Link` whose rendered href carries the locale prefix (for crawlers).
export const LocalizedLink = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      active,
      activeClassName = '',
      activeStyle = {},
      className = '',
      disabled,
      exact,
      hashScrollBehavior,
      href: hrefProp,
      onClick: onClickProp,
      onMouseEnter: onMouseEnterProp,
      prefetch,
      state,
      style = {},
      ...rest
    },
    ref
  ) => {
    const { onClick, onMouseEnter, href } = useLinkProps({
      disabled,
      hashScrollBehavior,
      href: hrefProp,
      onClick: onClickProp,
      onMouseEnter: onMouseEnterProp,
      prefetch,
      state,
    });
    const actualActive = useActive(href, { exact: !!exact });
    const isActive = active ?? actualActive;

    // Prefixed href = locale switch: full page load (navi would double the prefix).
    const isNativeLink =
      !!rest.target || (typeof href === 'string' && !!getLocaleFromPath(href));

    return (
      <a
        className={`${className} ${isActive ? activeClassName : ''}`}
        ref={ref}
        style={{ ...style, ...(isActive ? activeStyle : {}) }}
        {...rest}
        href={typeof href === 'string' ? localizeHref(href) : href}
        onClick={isNativeLink ? onClickProp : onClick}
        onMouseEnter={isNativeLink ? onMouseEnterProp : onMouseEnter}
      />
    );
  }
);

LocalizedLink.displayName = 'LocalizedLink';
